import { fetchGracenoteDefaults } from "core/hooks/useGracenoteData";
import { createContext, useEffect, useState, ReactNode, useContext } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Lineup, Sport } from "types";

export interface GracenoteContextType {
    values: {
        channels: Record<string, string[]>;
        sports: Sport[];
        lineups: Lineup[];
        sportMaxGames?: Record<string, number | "">;
        selectedTeams?: Record<string, number[]>;
        directTvEspn: boolean;
        fillGaps: boolean;
        triviaChannel: boolean;
        trivia_channel_description: string;
        trivia_channel_number: number | "";
    };
    change: (key: string, value: any) => void;
    submit: () => void;
    reset: () => void;
    isSubmitting: boolean;
    isDisable: boolean;
    setIsDisable: (value: boolean) => void;
    saveSelectedTeams: (selectedTeams: Record<string, number[]>, workplaceId: number, teamsMutation: any) => Promise<void>;
    errorMessage: string | null;
    setErrorMessage: (value: string) => void;
    setValues: React.Dispatch<React.SetStateAction<GracenoteContextType["values"]>>;
    offsetDays: number;
    setOffsetDays: React.Dispatch<React.SetStateAction<number>>;
}

const GracenoteContext = createContext<GracenoteContextType | undefined>(undefined);

interface GracenoteProviderProps {
    children: ReactNode;
}

export const GracenoteProvider = ({ children }: GracenoteProviderProps) => {
    const queryClient = useQueryClient();
    const workplaceId = Number(localStorage.getItem("workplace-id"));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDisable, setIsDisable] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [offsetDays, setOffsetDays] = useState<number>(0); // Default value is 0

    const [values, setValues] = useState<GracenoteContextType["values"]>({
        channels: {},
        sports: [],
        lineups: [],
        sportMaxGames: {},
        selectedTeams: {},
        directTvEspn: false,
        fillGaps: false,
        triviaChannel: false,
        trivia_channel_description: "",
        trivia_channel_number: "",
    });

    const { mutateAsync: fetchDefaults } = useMutation({
        mutationFn: () => fetchGracenoteDefaults(),
        onSuccess: (defaults) => {
            window.localStorage.setItem("gracenoteDefaults", JSON.stringify(defaults));
            // setValues((prev) => ({ ...prev, ...defaults }));
        },
        onError: (error) => {
            console.error("Failed to fetch Gracenote defaults:", error);
            setErrorMessage("Failed to load default settings");
        },
    });

    useEffect(() => {
        if (workplaceId) {
            fetchDefaults();
        }
    }, [workplaceId, fetchDefaults]);

    const change = (key: string, value: any) => {
        setValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const submit = async () => {
        setIsSubmitting(true);
        try {
            // Implement your submit logic here
            // For example, make an API call to save the values
            console.log("Submitting values:", values);
        } catch (error) {
            setErrorMessage("Failed to submit settings");
        } finally {
            setIsSubmitting(false);
        }
    };

    const reset = () => {
        setValues({
            channels: {},
            sports: [],
            lineups: [],
            sportMaxGames: {},
            selectedTeams: {},
            directTvEspn: false,
            fillGaps: false,
            triviaChannel: false,
            trivia_channel_description: "",
            trivia_channel_number: "",
        });
        setErrorMessage(null);
    };

    const saveSelectedTeams = async (
        selectedTeams: Record<string, number[]>,
        workplaceId: number,
        teamsMutation: any
    ) => {
        try {
            await teamsMutation({ selectedTeams, workplaceId });
            setValues((prev) => ({
                ...prev,
                selectedTeams,
            }));
        } catch (error) {
            setErrorMessage("Failed to save selected teams");
            throw error;
        }
    };

    const contextValue: GracenoteContextType = {
        values,
        change,
        submit,
        reset,
        isSubmitting,
        isDisable,
        setIsDisable,
        saveSelectedTeams,
        errorMessage,
        setErrorMessage,
        setValues,
        offsetDays,
        setOffsetDays
    };

    return (
        <GracenoteContext.Provider value={contextValue}>
            {children}
        </GracenoteContext.Provider>
    );
};

// Optional: Custom hook to use the context
export const useGracenoteContext = () => {
    const context = useContext(GracenoteContext);
    if (!context) {
        throw new Error("useGracenoteContext must be used within a GracenoteProvider");
    }
    return context;
};